import React from 'react';

import { Close as CloseIcon, WarningAmberOutlined as WarningIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Field } from 'react-final-form';
import { z } from 'zod';

import { Form } from '@hrlogics/react-components/form';

import { IntegrationScope, IntegrationsListDocument, useIntegrationCreateMutation } from 'src/api';
import { Modal } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useModalV2 } from 'src/hooks/shared';
import { ModalV2Props } from 'src/types/modalV2';
import { type RateLimitUnit, rateLimitDurationToMs } from 'src/utils/integrations';

import { CredentialsView } from './components/CredentialsView';
import { IntegrationVerifierField } from './components/IntegrationVerifierField';
import { RateLimitField } from './components/RateLimitField';
import { ScopeCardsField } from './components/ScopeCardField';
import { helperTextStyles, sectionLabelStyles } from './styles';

type VerifierOption = { id: string; displayName?: string | null };

type FormValues = {
  name: string;
  scope: IntegrationScope[];
  verifier: VerifierOption | null;
  ipLimitAttempts: number | null;
  ipLimitWindowValue: number | null;
  ipLimitWindowUnit: RateLimitUnit;
  credentialsLimitAttempts: number | null;
  credentialsLimitWindowValue: number | null;
  credentialsLimitWindowUnit: RateLimitUnit;
  verifierLimitAttempts: number | null;
  verifierLimitWindowValue: number | null;
  verifierLimitWindowUnit: RateLimitUnit;
};

type IssuedCredentials = {
  name: string;
  clientId: string;
  clientSecret: string;
};

const INITIAL_VALUES: FormValues = {
  name: '',
  scope: [],
  verifier: null,
  ipLimitAttempts: 1,
  ipLimitWindowValue: 1,
  ipLimitWindowUnit: 'min',
  credentialsLimitAttempts: 1,
  credentialsLimitWindowValue: 1,
  credentialsLimitWindowUnit: 'min',
  verifierLimitAttempts: 1,
  verifierLimitWindowValue: 1,
  verifierLimitWindowUnit: 'min',
};

const rateLimitField = z
  .number({ error: 'Must be a positive integer' })
  .int('Must be an integer')
  .positive('Must be greater than 0');

const validationSchema = z.object({
  name: z.string('Name is required').trim().nonempty('Name is required'),
  scope: z.array(z.enum(IntegrationScope)).min(1, 'At least one scope must be enabled'),
  verifier: z.object({ id: z.string() }).nullable(),
  ipLimitAttempts: rateLimitField,
  ipLimitWindowValue: rateLimitField,
  ipLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
  credentialsLimitAttempts: rateLimitField,
  credentialsLimitWindowValue: rateLimitField,
  credentialsLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
  verifierLimitAttempts: rateLimitField,
  verifierLimitWindowValue: rateLimitField,
  verifierLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
});

export const CreateIntegrationModal: React.FC<ModalV2Props<unknown>> = ({ id }) => {
  const { closeModal, switchClosability } = useModalV2();
  const { createApolloContext } = useNotification();
  const [credentials, setCredentials] = React.useState<IssuedCredentials | null>(null);

  const [createIntegration] = useIntegrationCreateMutation({
    context: createApolloContext({
      errorMessage: 'Failed to create integration',
    }),
    refetchQueries: [IntegrationsListDocument],
  });

  const onClose = React.useCallback(() => {
    closeModal(id);
    setCredentials(null);
  }, [closeModal, id]);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      switchClosability(false);
      try {
        const { data } = await createIntegration({
          variables: {
            name: values.name.trim(),
            scope: values.scope,
            ipLimitAttempts: values.ipLimitAttempts ?? 0,
            ipLimitWindow: rateLimitDurationToMs(
              values.ipLimitWindowValue ?? 0,
              values.ipLimitWindowUnit,
            ),
            credentialsLimitAttempts: values.credentialsLimitAttempts ?? 0,
            credentialsLimitWindow: rateLimitDurationToMs(
              values.credentialsLimitWindowValue ?? 0,
              values.credentialsLimitWindowUnit,
            ),
            verifierLimitAttempts: values.verifierLimitAttempts ?? 0,
            verifierLimitWindow: rateLimitDurationToMs(
              values.verifierLimitWindowValue ?? 0,
              values.verifierLimitWindowUnit,
            ),
            verifierId: values.verifier?.id ?? null,
          },
        });
        if (data?.integrationCreateCustom) {
          setCredentials({
            name: values.name.trim(),
            clientId: data.integrationCreateCustom.clientId,
            clientSecret: data.integrationCreateCustom.clientSecret,
          });
        }
      } finally {
        switchClosability(true);
      }
    },
    [createIntegration, switchClosability],
  );

  const closeButton = React.useMemo(
    () => ({
      show: true,
      actionName: 'close',
      onAction: onClose,
      render: () => (
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      ),
    }),
    [onClose],
  );

  if (credentials) {
    return (
      <Modal
        title={credentials.name}
        titleProps={{ component: 'div' }}
        topAction={closeButton}
        dialogProps={{ open: true, onClose }}
      >
        <CredentialsView
          alertTitle="Copy and share these now — you won't see them again"
          alertBody={
            <React.Fragment>
              The <strong>client secret</strong> is shown only once. Send these credentials securely
              to the partner integrating with us — they'll need both the client ID and secret to
              authenticate. If lost, you'll need to regenerate (which revokes the previous secret).
            </React.Fragment>
          }
          clientId={credentials.clientId}
          clientSecret={credentials.clientSecret}
          onClose={onClose}
        />
      </Modal>
    );
  }

  return (
    <Modal
      title="New inbound integration"
      topAction={closeButton}
      dialogProps={{ open: true, onClose }}
    >
      <Form<FormValues>
        onSubmit={onSubmit}
        subscription={{ submitting: true, valid: true }}
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
      >
        {({ submitting, valid }) => (
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Set up a new partner integration. We'll issue a dedicated <strong>client ID</strong>{' '}
              and <strong>client secret</strong> the partner will use to authenticate against our
              API.
            </Typography>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Integration name
              </Typography>
              <Field
                name="name"
                component={TextField}
                placeholder="e.g. Pinnacle Mortgage"
                fullWidth
                disabled={submitting}
              />
              <Typography variant="caption" color="text.secondary" sx={helperTextStyles}>
                Give this a recognizable name for the partner using it — you'll use this later to
                find their usage logs and identify which partner each request belongs to. Internal
                only; the partner won't see it.
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Verifier (optional)
              </Typography>
              <IntegrationVerifierField disabled={submitting} />
              <Typography variant="caption" color="text.secondary" sx={helperTextStyles}>
                Optionally tie this integration to a specific verifier. This can't be changed after
                creation.
              </Typography>
              <Typography variant="caption" color="warning" sx={helperTextStyles}>
                If no verifier is selected, this will not create verification requests — it will
                only share the data.
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Scope
              </Typography>
              <ScopeCardsField disabled={submitting} />
              <Typography variant="caption" color="text.secondary" sx={helperTextStyles}>
                Controls which verification types this partner can request.{' '}
                <strong>At least one</strong> must be enabled.
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Rate limits
              </Typography>
              <Stack spacing={2.5}>
                <RateLimitField
                  prefix="ip"
                  title="Per client IP"
                  description="Limits requests coming from the same source IP. Use 0 for either field to disable."
                  disabled={submitting}
                />
                <RateLimitField
                  prefix="credentials"
                  title="Per integration credentials"
                  description="Limits total requests authenticated with these credentials."
                  disabled={submitting}
                />
                <RateLimitField
                  prefix="verifier"
                  title="Per verifier user"
                  description="Limits requests per verifier_user_id supplied in the body."
                  disabled={submitting}
                />
              </Stack>
            </Box>

            <Alert
              icon={<WarningIcon fontSize="inherit" />}
              severity="warning"
              variant="outlined"
              sx={{ alignItems: 'flex-start' }}
            >
              A <strong>client ID</strong> and <strong>client secret</strong> will be issued on the
              next screen. The secret is shown <strong>only once</strong> — copy and share it
              securely with the partner before closing.
            </Alert>

            <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
              <Button variant="text" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting}
              >
                Create &amp; Issue Credentials
              </Button>
            </Box>
          </Stack>
        )}
      </Form>
    </Modal>
  );
};
